import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useError, useModules, useNavigation, usePlatform, useRouteChange, useUser } from 'hooks';
import { PageFrame, styled } from 'netline-ui';
import services from 'services';
import {
  AppHeader,
  ErrorView,
  LoadingProgress,
  MobileNavigation,
  NoConnection,
  OidcApplicationModule,
  SessionExpiredPage,
  SidebarNavigation,
} from 'view/components';

import styles, { appClasses } from './App.styles';

const AppRoot = styled(PageFrame, { name: appClasses.$name })(styles);

const App = () => {
  const { t } = useTranslation();
  const platformConfig = usePlatform();
  const { pluginsInitialized } = useModules();
  const { appModules, routedModules, defaultModule } = useNavigation();
  const { sessionIsExpired } = useUser();

  useRouteChange();

  useEffect(() => {
    services.appFrame.viewport.updateSingleApp(appModules);
  }, [appModules]);

  const singleApp = services.appFrame.viewport.isSingleApp();

  const singleAppRef = useRef(singleApp);
  singleAppRef.current = singleApp;

  const noModuleAccess = !defaultModule && !routedModules.length;

  const { main: error } = useError();
  useEffect(() => {
    if (!noModuleAccess || !pluginsInitialized || error) {
      return;
    }
    services.appFrame.error.main.addError({
      title: 'errors.access_denied.title',
      message: 'errors.access_denied.message',
      details: {
        type: 'NO_MODULE_ACCESS',
        timestamp: new Date(),
      },
    });
  }, [pluginsInitialized, noModuleAccess, error]);

  const modalRef = useRef<HTMLElement | null>(null);
  const setModalRef = useCallback((node) => {
    modalRef.current = node;
    services.appFrame.viewport.setMainNode(node);
  }, []);

  if (sessionIsExpired) {
    return <SessionExpiredPage />;
  }

  const hideSidebarNavigation = Boolean(
    !pluginsInitialized || singleApp || noModuleAccess || platformConfig.hideSidebarNavigation,
  );
  const hideMainViewport = Boolean(!pluginsInitialized || noModuleAccess || error);

  return (
    <AppRoot
      className={appClasses.pageFrame}
      data-testid="AppFrame-root"
      header={platformConfig.hideAppHeader ? <div /> : <AppHeader />}
      sidebar={!hideSidebarNavigation && <SidebarNavigation submenuContainerRef={modalRef} />}
    >
      {hideMainViewport && error && <ErrorView {...error} />}
      {hideMainViewport && !error && <LoadingProgress>{t('progress.initializing_plugins')}</LoadingProgress>}
      {!hideMainViewport && (
        <div className={appClasses.offlineView}>
          <div data-testid="App-moduleContainer" ref={setModalRef} className={appClasses.mainView}>
            {platformConfig.hideSidebarNavigation && <MobileNavigation />}
            <Routes>
              {routedModules.map((module) => (
                <Route
                  key={module.key}
                  path={`${module.route}${module.exactRoute ? '' : '/*'}`}
                  element={
                    <OidcApplicationModule
                      key={module.key}
                      className={appClasses.applicationModule}
                      moduleKey={module.key}
                      baseRoute={module.route}
                    />
                  }
                />
              ))}
              <Route path="*" element={defaultModule ? <Navigate to={defaultModule.route} replace /> : ''} />
            </Routes>
          </div>
          <NoConnection />
        </div>
      )}
    </AppRoot>
  );
};

export default App;
